import { Trans } from '@lingui/macro';
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

import { I18nProviderWrapper } from '@components/common/I18nProviderWrapper';

import { generateRandomPasskey } from '../../helpers/passkey';

import { PrivacySettingsColors } from './colors';

import CurrentPasskeySection from './CurrentPasskeySection';
import ExpirationDateSection from './ExpirationDateSection';
import ExpiredPasskeyAlert from './ExpiredPasskeyAlert';
import { useExpirationDate } from './hooks/useExpirationDate';
import { useFormValidation } from './hooks/useFormValidation';
import { usePasskeyExpiration } from './hooks/usePasskeyExpiration';
import { usePasskeySection } from './hooks/usePasskeySection';
import { useUIVisibility } from './hooks/useUIVisibility';
import PasskeyInput from './PasskeyInput';
import PasswordDigestAlert from './PasswordDigestAlert';
import SuccessMessage from './SuccessMessage';

// Import types
import { PasswordSectionProps } from './types';

// Extend Window interface for global functions
declare global {
  interface Window {
    showPasskeySectionSuccess?: () => void;
  }
}

// ===== STYLED COMPONENTS =====

const CancelButton = styled.button`
  background: none;
  border: 1px solid ${PrivacySettingsColors.ErrorBorder};
  color: ${PrivacySettingsColors.ErrorBorder};
  cursor: pointer;
  font-size: 14px;
  padding: 8px 16px;
  border-radius: 4px;
  margin-top: 10px;

  &:hover {
    background-color: ${PrivacySettingsColors.LightGrayBackground};
  }
`;

const GuestPasswordContainer = styled.span`
  display: block;

  /* Add any additional styling that was previously applied to .j-guest-password */
  /* The j-guest-password class appears to be primarily a JavaScript hook */
  /* If additional styling is needed, it can be added here */
`;

// ===== MAIN COMPONENT =====

/**
 * PasswordSection Component - Manages passkey creation, update, and display
 *
 * Handles both legacy password system and new passkey system with:
 * - Date format conversion (MM/DD/YYYY display ↔ YYYY/MM/DD server)
 * - Expiration checking and alerts
 * - Random passkey generation with length validation
 * - Form submission integration with privacy settings
 */
const PasswordSection: React.FunctionComponent<PasswordSectionProps> = ({
  whitelabelPresenter = null,
  currentPasskey = '',
  expirationDate = '',
  contextType = 'brandfolder',
  currentPasswordDigest = ''
}) => {
  // ===== CUSTOM HOOKS =====

  // Complex logic extracted to custom hooks
  const {
    expirationOption,
    customDate,
    expiryDate,
    expirationOptions,
    handleExpirationChange,
    handleCustomDateChange
  } = useExpirationDate();

  const {
    showCurrentPasskey,
    showNewPasskeyField,
    setShowNewPasskeyField,
    showSuccessMessage,
    showCancelButton,
    setShowCancelButton,
    togglePasskeyVisibility,
    handleChangePasskey
  } = useUIVisibility();

  const { isPasskeySectionEnabled: isGuestPasskeySectionEnabled } = usePasskeySection(contextType, currentPasskey);
  const { displayExpirationDate, isPasskeyExpired } = usePasskeyExpiration(expirationDate);

  // ===== SIMPLE STATE & LOGIC (kept inline) =====

  // Passkey generation and state - simple enough to keep here
  const [passkey, setPasskey] = useState<string>('');

  const handleGeneratePasskey = useCallback((): void => {
    const newPasskey = generateRandomPasskey();
    setPasskey(newPasskey);
  }, []);

  // Auto-enable logic for fresh setups - simple conditional logic
  useEffect(() => {
    const hasPasswordDigest = !currentPasskey && currentPasswordDigest && !!expirationDate;
    const hasExpiredPasskey = isPasskeyExpired && currentPasskey;
    const hasCurrentPasskey = currentPasskey && !isPasskeyExpired;

    // Enable input field by default for fresh passkey setup (no alerts to show)
    if (!hasPasswordDigest && !hasExpiredPasskey && !hasCurrentPasskey) {
      setShowNewPasskeyField(true);
      setShowCancelButton(false); // Hide cancel button if it's default view
    }
  }, [
    currentPasskey,
    currentPasswordDigest,
    isPasskeyExpired,
    expirationDate,
    setShowNewPasskeyField,
    setShowCancelButton
  ]);

  useEffect(() => {
    if (showNewPasskeyField && !passkey) {
      handleGeneratePasskey();
    }
  }, [showNewPasskeyField, handleGeneratePasskey, passkey]);

  // Form validation using custom hook
  useFormValidation(
    contextType,
    passkey,
    showNewPasskeyField,
    isGuestPasskeySectionEnabled,
    expirationOption,
    customDate
  );

  /**
   * Gets the appropriate library name for display
   */
  const getLibraryName = (): string => {
    return whitelabelPresenter?.library_name ||
           (contextType === 'collection' ? 'Collection' : 'Brandfolder');
  };

  // ===== MAIN RENDER =====

  return (
    <I18nProviderWrapper>
      <div>
        {isGuestPasskeySectionEnabled ? (
          <>
            {/* Success Message - when passkey changes are saved */}
            {showSuccessMessage && expiryDate && passkey && (
              <SuccessMessage
                expiryDate={expiryDate}
                onChangePasskey={handleChangePasskey}
                onToggleVisibility={togglePasskeyVisibility}
                passkey={passkey}
                showCurrentPasskey={showCurrentPasskey}
                showNewPasskeyField={showNewPasskeyField}
              />
            )}

            {/* Main content when not showing success message */}
            {!showSuccessMessage && (
              <>
                {/* Password Digest Alert - when no passkey but password digest exists */}
                {!currentPasskey && currentPasswordDigest && !!expirationDate && !isPasskeyExpired && (
                  <PasswordDigestAlert
                    displayExpirationDate={displayExpirationDate}
                    libraryName={getLibraryName()}
                    onUpdateClick={handleChangePasskey}
                    showNewPasskeyField={showNewPasskeyField}
                  />
                )}

                {/* Expired Passkey Alert - when existing passkey is expired */}
                {isPasskeyExpired && (currentPasskey || currentPasswordDigest) && (
                  <ExpiredPasskeyAlert
                    displayExpirationDate={displayExpirationDate}
                    libraryName={getLibraryName()}
                    onUpdateClick={handleChangePasskey}
                    showNewPasskeyField={showNewPasskeyField}
                  />
                )}

                {/* Current Passkey Section - when passkey exists and not expired */}
                {currentPasskey && !isPasskeyExpired && (
                  <CurrentPasskeySection
                    currentPasskey={currentPasskey}
                    displayExpirationDate={displayExpirationDate}
                    libraryName={getLibraryName()}
                    onChangePasskey={handleChangePasskey}
                    onToggleVisibility={togglePasskeyVisibility}
                    showCurrentPasskey={showCurrentPasskey}
                    showNewPasskeyField={showNewPasskeyField}
                  />
                )}

                {/* Passkey Input Fields - shown when creating/updating passkey */}
                {showNewPasskeyField && (
                  <GuestPasswordContainer className="j-guest-password">
                    <PasskeyInput
                      contextType={contextType}
                      expiryDate={expiryDate}
                      onGeneratePasskey={handleGeneratePasskey}
                      passkey={passkey}
                    />
                    <ExpirationDateSection
                      customDate={customDate}
                      expirationOption={expirationOption}
                      expirationOptions={expirationOptions}
                      onCustomDateChange={handleCustomDateChange}
                      onExpirationChange={handleExpirationChange}
                    />
                    {showCancelButton && (
                      <CancelButton
                        onClick={() => setShowNewPasskeyField(false)}
                        type="button"
                      >
                        <Trans>Cancel</Trans>
                      </CancelButton>
                    )}
                  </GuestPasswordContainer>
                )}
              </>
            )}
          </>
        ) : null}
      </div>
    </I18nProviderWrapper>
  );
};

export default PasswordSection;
